/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/**
 * Server-provided guess of the device class, derived from the User-Agent.
 * It drives `isMobileWidth` during SSR and the first client render (both see
 * the same value, so hydration stays consistent); actual measurements take
 * over after mount. Without a hint the legacy mobile-first default applies.
 */
export type DeviceHint = 'mobile' | 'desktop';

const DeviceHintContext = createContext<DeviceHint | null>(null);

export function DeviceHintProvider({
  hint,
  children,
}: {
  hint?: DeviceHint | null;
  children: ReactNode;
}) {
  // Plain .ts module — no JSX.
  return createElement(
    DeviceHintContext.Provider,
    { value: hint ?? null },
    children,
  );
}

export function isWebUserAgent() {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;

  return /android|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(ua);
}

/**
 * Returns information about the device and its environment.
 * @returns {Object} An object containing information about the device and its environment.
 * @example
 * const { isWeb, isMobileBrowser, isMobileWidth } = useDeviceInfo();
 *
 * if (isWeb && isMobileBrowser) {
 *   return <MobileWebUI />;
 * }
 * return <DesktopUI />;
 */
export function useDeviceInfo(containerWidth?: number) {
  const { width, height } = useWindowDimensions();
  const deviceHint = useContext(DeviceHintContext);

  const isWeb = Platform.OS === 'web';
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setIsMobileBrowser(isWeb && isWebUserAgent());
    setHydrated(true);
  }, [isWeb]);

  // Before hydration (server render AND first client render — identical, so
  // hydration stays consistent) the server's User-Agent hint decides the
  // device class; without a hint the legacy mobile-first default applies.
  // Once mounted, the actual browser/screen measurements take over.
  const hintIsMobile = deviceHint ? deviceHint === 'mobile' : true;
  const resolvedWidth = hydrated ? width : 0;
  const resolvedHeight = hydrated ? height : 0;
  const isMobileWidth = hydrated
    ? (containerWidth ?? width) < 768
    : hintIsMobile;

  return {
    isWeb,
    isMobileBrowser,
    isMobileWidth,
    width: resolvedWidth,
    height: resolvedHeight,
  };
}

/**
 * Returns the width of the container.
 */
export function useContainerWidth() {
  const [containerWidth, setWidth] = useState<number>(0);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setWidth(width);
  }, []);

  return { containerWidth, onLayout };
}
